<template>
  <UCalendar
    v-model="calendarValue"
    :week-starts-on="weekStartsOn"
    :locale="$i18n.locale"
    class="border-0 bg-transparent"
    @update:placeholder="handlePlaceholderChange"
  >
    <template #day="{ day }">
      <div
        class="relative flex items-center justify-center w-8 h-8 cursor-pointer group"
      >
        <div class="absolute inset-0 rounded-lg bg-gray-200/85 opacity-0 group-hover:opacity-100 transition-all dark:bg-gray-800" />

        <div
          v-if="isToday(day)"
          class="absolute inset-0 rounded-lg bg-red-500 transition-all"
        />

        <div
          v-if="isSelected(day)"
          class="absolute inset-0 rounded-lg bg-primary-600 transition-all"
        />

        <span
          :class="[
            'relative z-10 text-sm font-medium transition-all',
            isToday(day) || isSelected(day)
              ? 'text-white dark:text-black'
              : 'text-gray-700 dark:text-gray-200',
          ]"
        >
          {{ day.day }}
        </span>

        <div
          v-if="getAvailabilityDot(day)"
          class="absolute inset-x-0 bottom-0 px-1 z-30"
        >
          <div
            class="rounded-full h-1 w-1 mx-auto mb-0.5"
            style="background-color: #8dba60"
          />
        </div>
      </div>
    </template>
  </UCalendar>
</template>

<script setup lang="ts">
import { CalendarDate, today as calendarToday, getLocalTimeZone, isEqualDay, isToday as isCalendarToday, type DateValue } from '@internationalized/date'
import { AvailabilityRequest, AvailabilityRequestDateMode, AvailabilityRequestGroupMode } from '@/_api/models/booking'

const { $api, $auth, $date, $dateFns } = useNuxtApp()

interface Props {
  modelValue?: Date | null
  services: any[]
  reservationCustomer?: object | null
  includeEmployees?: boolean
  groupMode?: any
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  reservationCustomer: null,
  includeEmployees: true,
  groupMode: () => AvailabilityRequestGroupMode.reservation,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const availableDays = ref<Set<string>>(new Set())
let latestAvailabilityRequest = 0

const todayDate = calendarToday(getLocalTimeZone())
const initialPage = props.modelValue ? dateToCalendarDate(props.modelValue) : todayDate
const currentPage = ref({ month: initialPage.month, year: initialPage.year })
const weekStartsOn = useCalendarFirstDayOfWeek(() => $auth?.info?.location?.startDayOfWeek)

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day)
}

const calendarValue = computed({
  get(): DateValue | undefined {
    if (!props.modelValue) return undefined
    return dateToCalendarDate(props.modelValue)
  },
  set(value: DateValue | undefined) {
    emit('update:modelValue', value ? calendarDateToDate(value) : null)
  },
})

function isToday(day: DateValue): boolean {
  return isCalendarToday(day, getLocalTimeZone())
}

function isSelected(day: DateValue): boolean {
  if (!calendarValue.value) return false
  return isEqualDay(day, calendarValue.value)
}

function getAvailabilityDot(day: DateValue): boolean {
  const key = `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`
  return availableDays.value.has(key)
}

function handlePlaceholderChange(placeholder: DateValue) {
  const month = placeholder.month
  const year = placeholder.year
  if (!currentPage.value || currentPage.value.month !== month || currentPage.value.year !== year) {
    currentPage.value = { month, year }
    getAvailability()
  }
}

watch(() => props.includeEmployees, () => {
  getAvailability()
})

watch(() => props.services, () => {
  getAvailability()
}, { deep: true })

onMounted(() => {
  getAvailability()
})

async function getAvailability() {
  const requestId = ++latestAvailabilityRequest
  const { month, year } = currentPage.value
  const start = new Date(year, month - 1)
  const dateObj = new Date(start.getTime())
  const length = $dateFns.getDaysInMonth(dateObj)
  const days: any[] = []

  let day = $date.getStartOfDayUTC(dateObj)

  while (days.length < length) {
    days.push(day)
    day = $date.add(day, { days: 1 })
  }

  const reservation = {
    services: props.services,
  }

  const includeEmployees = props.includeEmployees
  const groupMode = props.groupMode
  const request = AvailabilityRequest.forReservation(
    reservation,
    days,
    { includeEmployees, groupMode },
  )

  request.dateMode = AvailabilityRequestDateMode.onePerDay
  request.includeSlots = false

  // `$api.booking.availability` returns `ApiResponse<AvailabilityResponse>` —
  // unwrap `.data` to reach the dates array. Previously this iterated
  // `availability.dates` directly which was always undefined → TypeError.
  const response = await $api.booking.availability(request)

  if (requestId !== latestAvailabilityRequest) return

  const availability = response?.data

  const newSet = new Set<string>()
  for (const item of availability?.dates ?? []) {
    if (item.availability) {
      const d = $date.utc(item.date)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      newSet.add(key)
    }
  }
  availableDays.value = newSet
}

onBeforeUnmount(() => {
  latestAvailabilityRequest += 1
})
</script>
